import { FC, useMemo, useState } from "react"
import { css } from "@emotion/react"
import { IconButton, TextField } from "@material-ui/core"
import { Autocomplete, AutocompleteChangeReason } from "@material-ui/lab"
import { useHistory, useRouteMatch } from "react-router-dom"
import { useDebounce } from "@uidotdev/usehooks"
import {
	CustomerPart,
	initialCustomerPartQueryParams,
	useSearchOnlinePartsEcomm,
} from "@ncs/ncs-api"
import {
	Box,
	encodeUrlState,
	Icon,
	HighlightedParagraph,
	PartImage,
	useChangeCallback,
	useUrlState,
	TrackingEvent,
	VisualSearchCameraIcon,
	handleVisualSearch,
} from "@ncs/web-legos"
import { PartSearchResultsUrlState } from "~/views/shop/part-search-results"
import { iconCss, autocompleteStyle, optionImageStyle } from "./styles"
import { useAnalytics } from "~/hooks/useAnalytics"

type ShopBarSearchUrlState = Pick<PartSearchResultsUrlState, "search"> &
	Partial<
		Pick<
			PartSearchResultsUrlState,
			| "page"
			| "manufacturers"
			| "categories"
			| "systems"
			| "chemicals"
			| "brands"
			| "price_Gte"
			| "price_Lt"
			| "featured"
			| "clearance"
			| "ordering"
			| "priceRanges"
		>
	>

export const ShopBarSearch: FC = () => {
	const history = useHistory()
	const [popoverOpen, setPopoverOpen] = useState(false)
	const onSearchPage = useRouteMatch("/shop/search")
	const { trackEvent } = useAnalytics()

	const [{ search: urlSearch }, { setUrlState }] = useUrlState<ShopBarSearchUrlState>({
		search: initialCustomerPartQueryParams.search,
	})

	const [inputValue, setInputValue] = useState<string | null>(urlSearch)
	const debouncedSearchTerm = useDebounce(inputValue, 500)

	// We'll control the state of the autocomplete here so that we have access
	// to what's selected when you click the 'search' button.
	const [autocompleteValue, setAutocompleteValue] = useState<CustomerPart | string | null>(null)

	const { data: results, isLoading } = useSearchOnlinePartsEcomm({
		params: {
			search: debouncedSearchTerm,
			pageSize: 100,
		},
		queryConfig: {
			enabled: !!debouncedSearchTerm,
			keepPreviousData: true,
		},
		removeChildren: true,
	})

	const partResults = useMemo(() => {
		const items = results ?? []
		const term = (debouncedSearchTerm ?? "").toLowerCase().trim()

		if (term.length === 0) return items

		const startsWith = []
		const contains = []
		const others = []

		for (const item of items) {
			const title = (item?.title ?? "").toLowerCase()
			if (title.startsWith(term)) {
				startsWith.push(item)
			} else if (title.includes(term)) {
				contains.push(item)
			} else {
				others.push(item)
			}
		}

		return [...startsWith, ...contains, ...others]
	}, [results, debouncedSearchTerm])

	const getOptionLabel = (option: CustomerPart | string | null) => {
		if (option == null) {
			return ""
		} else if (typeof option === "string") {
			return option
		} else {
			return `${option.title ?? ""} ${option.onlinePartNumber ?? ""}`
		}
	}

	const onSearch = (searchValue: CustomerPart | string | null) => {
		if (searchValue == null) {
			history.push("/shop/search")
		} else if (typeof searchValue === "string") {
			if (onSearchPage) {
				// Reset ALL filters when changing search term
				setUrlState({
					search: searchValue,
					page: 1,
					manufacturers: undefined,
					categories: undefined,
					systems: undefined,
					chemicals: undefined,
					brands: undefined,
					price_Gte: undefined,
					price_Lt: undefined,
					featured: undefined,
					clearance: undefined,
					ordering: undefined,
					priceRanges: undefined,
				})
			} else {
				// Only send search and page when navigating to search page
				history.push({
					pathname: "/shop/search/",
					search: encodeUrlState<PartSearchResultsUrlState>({
						search: searchValue,
						page: 1,
					}),
				})
			}
			setInputValue(searchValue)
			trackEvent(TrackingEvent.SEARCH, { search_value: searchValue })
		} else {
			history.push(`/shop/parts/${searchValue.id}`)
			trackEvent(TrackingEvent.GO_TO_PRODUCT, { id: searchValue.id })
			trackEvent(TrackingEvent.SEARCH, { search_value: searchValue.description })
		}
	}

	const onChangeSelection = (
		option: CustomerPart | string | null,
		reason: AutocompleteChangeReason
	) => {
		// Update local state.
		setAutocompleteValue(option)

		if (typeof option === "string") {
			setInputValue(option)
		} else if (reason === "clear") {
			setInputValue("")
		}

		if (reason !== "clear") {
			onSearch(option)
		}
	}

	useChangeCallback(urlSearch, (newSearch) => {
		const currentLabel = autocompleteValue ? getOptionLabel(autocompleteValue) : ""
		if (newSearch !== inputValue && newSearch !== currentLabel) {
			setInputValue(newSearch ?? null)
			setAutocompleteValue(null) // Reset autocomplete selection if URL changes externally
		}
	})

	const placeholder = "Search Product"

	const handleSearch = () => {
		setPopoverOpen(false)
		onSearch(inputValue)
	}

	const handleVisualSearchOnSearchBar = (e: unknown) => {
		trackEvent(TrackingEvent.VISUAL_SEARCH_BAR)
		handleVisualSearch()
	}

	return (
		<Autocomplete
			freeSolo
			fullWidth
			options={partResults || []}
			open={popoverOpen}
			css={autocompleteStyle}
			onClose={() => setPopoverOpen(false)}
			getOptionLabel={getOptionLabel}
			onInputChange={(e, value, reason) => {
				if (reason === "input") {
					setInputValue(value)
					setPopoverOpen(!!value)
				}
			}}
			inputValue={inputValue ?? ""}
			value={inputValue}
			onChange={(e, option, reason) => onChangeSelection(option, reason)}
			renderInput={(params) => (
				<TextField
					className="search-input-text"
					data-testid="search-input-text"
					{...params}
					inputProps={{
						...params.inputProps,
						onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
							const original = params.inputProps as any
							if (original && typeof original.onKeyDown === "function") {
								original.onKeyDown(e)
							}
							if (e.key === "Enter") {
								handleSearch()
							}
						},
					}}
					InputProps={{
						...params.InputProps,
						disableUnderline: true,
						placeholder,
						endAdornment: (
							<>
								<IconButton
									css={iconCss}
									onClick={handleSearch}
									type="button"
									aria-label={
										isLoading && !!debouncedSearchTerm ?
											"Searching..."
										:	"Search"
									}
									data-testid="search-button"
								>
									<Icon
										icon={
											isLoading && !!debouncedSearchTerm ?
												"spinner-third"
											:	"search"
										}
										color="gray"
										spin={isLoading && !!debouncedSearchTerm}
										aria-hidden="true"
									/>
								</IconButton>

								<IconButton
									role="button"
									tabIndex={0}
									id="visual-search-camera"
									onClick={handleVisualSearchOnSearchBar}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ")
											handleVisualSearchOnSearchBar(e)
									}}
									data-testid="visual-search-camera"
									aria-label="Open visual search with camera"
									css={css`
										margin-top: -0.2rem;
										cursor: pointer;
									`}
								>
									<VisualSearchCameraIcon />
								</IconButton>
							</>
						),
					}}
				/>
			)}
			onOpen={() => {
				if (inputValue) {
					setPopoverOpen(true)
				}
			}}
			renderOption={(option: any) => {
				if (typeof option === "string" || !option?.id) {
					return null
				}
				return (
					<Box
						display="flex"
						alignItems="center"
						key={option.id}
						data-testid="autocomplete-option"
					>
						<PartImage
							src={option.imageUrl}
							width={3}
							css={optionImageStyle}
							alt={option.title}
						/>
						<Box display="flex" flexDirection="column" ml={1}>
							<HighlightedParagraph
								text={option.title}
								highlightedText={debouncedSearchTerm}
							/>
							<HighlightedParagraph
								small
								color="secondary"
								text={
									option.onlinePartNumber ? `#${option.onlinePartNumber}`
									: option.isParent ?
										"Multiple options available"
									:	""
								}
								highlightedText={debouncedSearchTerm}
							/>
						</Box>
					</Box>
				)
			}}
		/>
	)
}
